// ================================================================================
// ECOMSIMPLY - BLUE-OCEAN DRAWER COMPONENT
// Drawer détaillé pour opportunités Blue-Ocean avec blueprint et supplier brief
// ================================================================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Eye, ExternalLink, Download, Star, TrendingUp, 
  Target, BarChart3, Zap, Factory, FileText, Users, 
  Globe, CheckCircle, AlertCircle, Copy
} from 'lucide-react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

const BlueOceanDrawer = ({ opportunity, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !opportunity) return null;

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 55) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 85) return 'bg-green-100 text-green-700 border-green-200';
    if (score >= 70) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (score >= 55) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Eye },
    { id: 'blueprint', label: 'Blueprint', icon: FileText },
    { id: 'suppliers', label: 'Fournisseurs', icon: Factory },
    { id: 'evidence', label: 'Evidence', icon: BarChart3 }
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
        
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl font-bold truncate">
                    {opportunity.title}
                  </h2>
                  <Badge className="bg-white bg-opacity-20 text-white border-white border-opacity-30">
                    ASIN: {opportunity.asin}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-sm">
                  <span>Prix: {opportunity.price_buybox?.toFixed(2)}€</span>
                  <span>Rating: {opportunity.rating?.toFixed(1)}/5</span>
                  <span>BSR: {opportunity.bsr?.toLocaleString() || 'N/A'}</span>
                </div>
                
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="bg-white bg-opacity-20 text-white">
                    Bankability: {(opportunity.scores?.bankability || 0).toFixed(0)}
                  </Badge>
                  <Badge className="bg-white bg-opacity-20 text-white">
                    Risk: {(opportunity.scores?.risk_index || 0).toFixed(0)}
                  </Badge>
                  <Badge className="bg-white bg-opacity-20 text-white">
                    {opportunity.scores?.trend_signal || 'neutral'}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                {opportunity.asin && (
                  <Button
                    onClick={() => window.open(`https://amazon.fr/dp/${opportunity.asin}`, '_blank')}
                    variant="outline"
                    size="sm"
                    className="text-white border-white hover:bg-white hover:text-blue-600"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  onClick={onClose}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white hover:bg-opacity-20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation tabs */}
          <div className="border-b border-gray-200 bg-gray-50">
            <nav className="flex px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-4 text-sm font-medium border-b-2 ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Scores Blue-Ocean */}
                <Card className="p-4">
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    Scores Blue-Ocean Propriétaires
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getScoreColor(opportunity.scores?.ess || 0)}`}>
                        {(opportunity.scores?.ess || 0).toFixed(0)}
                      </div>
                      <div className="text-sm text-gray-600">ESS</div>
                      <div className="text-xs text-gray-500">Empty-Shelf</div>
                    </div>
                    
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getScoreColor(opportunity.scores?.dgs || 0)}`}>
                        {(opportunity.scores?.dgs || 0).toFixed(0)}
                      </div>
                      <div className="text-sm text-gray-600">DGS</div>
                      <div className="text-xs text-gray-500">Dominance-Gap</div>
                    </div>
                    
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getScoreColor(opportunity.scores?.fmi || 0)}`}>
                        {(opportunity.scores?.fmi || 0).toFixed(0)}
                      </div>
                      <div className="text-sm text-gray-600">FMI</div>
                      <div className="text-xs text-gray-500">First-Mover</div>
                    </div>
                    
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getScoreColor(opportunity.scores?.sri || 0)}`}>
                        {(opportunity.scores?.sri || 0).toFixed(0)}
                      </div>
                      <div className="text-sm text-gray-600">SRI</div>
                      <div className="text-xs text-gray-500">Supplier Ready</div>
                    </div>
                  </div>
                </Card>

                {/* Détails produit */}
                <Card className="p-4">
                  <h3 className="text-lg font-medium mb-4">Détails du produit</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Prix de vente</label>
                      <div className="text-lg font-medium">{opportunity.price_buybox?.toFixed(2)}€</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Nombre d'avis</label>
                      <div className="text-lg font-medium">{opportunity.reviews_count?.toLocaleString() || 0}</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Nombre d'offres</label>
                      <div className="text-lg font-medium">{opportunity.offers_count || 0}</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Compliance</label>
                      <Badge className={
                        opportunity.scores?.compliance === 'low' ? 'bg-green-100 text-green-700' :
                        opportunity.scores?.compliance === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        opportunity.scores?.compliance === 'high' ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {opportunity.scores?.compliance || 'unknown'}
                      </Badge>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {activeTab === 'blueprint' && (
              <div className="space-y-6">
                <Card className="p-4">
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Blueprint Produit GPT-5
                  </h3>
                  
                  {opportunity.blueprint ? (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">USP - Proposition de Valeur Unique</label>
                        <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm">
                          {opportunity.blueprint.usp || 'Non disponible'}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700">Spécifications Améliorées</label>
                        <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm">
                          {opportunity.blueprint.specs || 'Non disponible'}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700">Stratégie Packaging</label>
                        <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm">
                          {opportunity.blueprint.packaging || 'Non disponible'}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700">Points Conformité</label>
                        <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm">
                          {opportunity.blueprint.compliance_check || 'Non disponible'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Blueprint GPT non généré</p>
                      <p className="text-sm">Lancez un scan avec deep_analysis pour générer le blueprint</p>
                    </div>
                  )}
                </Card>
              </div>
            )}

            {activeTab === 'suppliers' && (
              <div className="space-y-6">
                <Card className="p-4">
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <Factory className="w-5 h-5 text-blue-600" />
                    Fournisseurs Vérifiés ({opportunity.suppliers?.length || 0})
                  </h3>
                  
                  {opportunity.suppliers && opportunity.suppliers.length > 0 ? (
                    <div className="space-y-4">
                      {opportunity.suppliers.map((supplier, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium">{supplier.company_name || `Fournisseur ${index + 1}`}</h4>
                            <Badge className="bg-blue-100 text-blue-700">
                              {supplier.verification_level || 'Verified'}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Prix:</span>
                              <div className="font-medium">
                                {supplier.price_min?.toFixed(2)}€ - {supplier.price_max?.toFixed(2)}€
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-600">MOQ:</span>
                              <div className="font-medium">{supplier.min_order_quantity || 'N/A'}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Délai:</span>
                              <div className="font-medium">{supplier.lead_time_days || 'N/A'} jours</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Rating:</span>
                              <div className="font-medium flex items-center gap-1">
                                {supplier.supplier_rating || 'N/A'}
                                <Star className="w-3 h-3 text-yellow-400" />
                              </div>
                            </div>
                          </div>
                          
                          {supplier.verified_flags && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {supplier.verified_flags.map((flag, flagIndex) => (
                                <Badge key={flagIndex} className="bg-green-100 text-green-700 text-xs">
                                  {flag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Factory className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Aucun fournisseur trouvé</p>
                      <p className="text-sm">Lancez un scan avec include_suppliers pour trouver des fournisseurs</p>
                    </div>
                  )}
                </Card>

                {/* Supplier Brief */}
                {opportunity.supplier_brief && (
                  <Card className="p-4">
                    <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-600" />
                      Brief Fournisseur (Prêt à l'envoi)
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-700">Version Française</span>
                          <Button size="sm" variant="outline" className="flex items-center gap-1">
                            <Copy className="w-3 h-3" />
                            Copier
                          </Button>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-sm max-h-32 overflow-y-auto">
                          {opportunity.supplier_brief.fr || 'Non disponible'}
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-700">English Version</span>
                          <Button size="sm" variant="outline" className="flex items-center gap-1">
                            <Copy className="w-3 h-3" />
                            Copy
                          </Button>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg text-sm max-h-32 overflow-y-auto">
                          {opportunity.supplier_brief.en || 'Not available'}
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'evidence' && (
              <div className="space-y-6">
                <Card className="p-4">
                  <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    Evidence Board
                  </h3>
                  
                  {opportunity.evidence ? (
                    <div className="space-y-4">
                      {/* Signaux d'opportunité */}
                      {opportunity.evidence.opportunity_signals && (
                        <div>
                          <h4 className="font-medium mb-2">Signaux d'Opportunité</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(opportunity.evidence.opportunity_signals).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2">
                                {value ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-red-500" />
                                )}
                                <span className="text-sm capitalize">
                                  {key.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Gaps de marché */}
                      {opportunity.evidence.market_gaps && (
                        <div>
                          <h4 className="font-medium mb-2">Gaps de Marché</h4>
                          <div className="space-y-2">
                            {Object.entries(opportunity.evidence.market_gaps).map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="text-sm capitalize">{key.replace(/_/g, ' ')}:</span>
                                <span className="text-sm font-medium">{value?.toFixed(1) || 'N/A'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Points de douleur */}
                      {opportunity.evidence.pain_points && opportunity.evidence.pain_points.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2">Points de Douleur Identifiés</h4>
                          <ul className="space-y-1">
                            {opportunity.evidence.pain_points.map((point, index) => (
                              <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                                <span className="text-red-500 mt-1">•</span>
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>Evidence non disponible</p>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Source: {opportunity.source || 'rainforest+gpt'} • 
                Créé: {opportunity.created_at ? new Date(opportunity.created_at).toLocaleDateString() : 'N/A'}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export PDF
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  size="sm"
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BlueOceanDrawer;